import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, URL_LANG_PARAM, normalizeLocale, resolvePreferredLocale } from '@gift-i18n';
import type { RunnerType, Session } from '@claude-code-api/shared';

export const PM_CAPABLE_RUNNERS: RunnerType[] = ['acpx', 'acpx-experimental'];

export function isPmCapableRunner(runnerType: RunnerType | string | null | undefined): boolean {
  return runnerType === 'acpx' || runnerType === 'acpx-experimental';
}

export function isRunningPmSession(session: Pick<Session, 'runnerType' | 'status'>): boolean {
  return session.status === 'running' && isPmCapableRunner(session.runnerType);
}

function resolveCurrentLocale(): string {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  return resolvePreferredLocale({
    search: window.location.search,
    storage: window.localStorage,
    languages: window.navigator.languages,
    fallback: DEFAULT_LOCALE,
  });
}

function attachLocaleParam(params: URLSearchParams): void {
  const locale = normalizeLocale(params.get(URL_LANG_PARAM)) || normalizeLocale(resolveCurrentLocale()) || DEFAULT_LOCALE;
  if (locale !== DEFAULT_LOCALE || params.has(URL_LANG_PARAM)) {
    params.set(URL_LANG_PARAM, locale);
  }
  try {
    window.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage write failures
  }
}

export function getSessionOpenUrl(sessionId: string, options?: { shared?: boolean }): string {
  const params = new URLSearchParams();
  attachLocaleParam(params);
  const query = params.toString();
  const basePath = options?.shared ? `/api/sessions/${sessionId}/share/open` : `/api/sessions/${sessionId}/open`;
  return query ? `${basePath}?${query}` : basePath;
}

export function getGiftAgentWorkspaceUrl(sessionId: string, previewUrl?: string | null, mode: 'workspace' | 'pm' = 'workspace'): string {
  if (!previewUrl && mode === 'workspace') {
    return `/agent/${sessionId}`;
  }
  const params = new URLSearchParams();
  if (previewUrl) params.set('preview', previewUrl);
  if (mode === 'pm') params.set('mode', 'pm');
  attachLocaleParam(params);
  const query = params.toString();
  return `/api/acpx/sessions/${sessionId}/ui/${query ? `?${query}` : ''}`;
}

export function getDirectGiftAgentUrl(options: {
  agentUrl?: string | null;
  sessionId: string;
  previewUrl?: string | null;
  mode?: 'workspace' | 'pm';
  bootstrapToken?: string | null;
  shared?: boolean;
}): string | null {
  const agentUrl = String(options.agentUrl || '').trim();
  if (!agentUrl) return null;
  const params = new URLSearchParams();
  params.set('sessionId', options.sessionId);
  if (options.previewUrl) params.set('preview', options.previewUrl);
  if (options.mode === 'pm') params.set('mode', 'pm');
  if (options.bootstrapToken) params.set('authToken', options.bootstrapToken);
  if (options.shared) params.set('shared', '1');
  attachLocaleParam(params);
  return `${agentUrl.replace(/\/$/, '')}/ui/?${params.toString()}`;
}
